import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  CircularProgress,
  LinearProgress,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useNavigate, useParams } from "react-router-dom";
import { useApiRequest } from "../Login/Hooks/useApiRequest";
import { useWebSocket } from "../../Websocket/useWebSocket";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import EmailVerifySheet from "../DigitalClaim/EmailVerifySheet";
import SuccessClaimedDialog from "../DigitalClaim/SuccessClaimedDialog";
import logger from "../../Utils/logger";

// ─────────────────────────────────────────────
// ITEM STATUS HELPERS — add more conditions here
// ─────────────────────────────────────────────

/**
 * Determines the vend status of a PHYSICAL item.
 * success: true  → dispensed ✅
 * success: false → failed ❌
 * success: null  → in progress ⏳
 */
const getPhysicalStatus = (item) => {
  if (item.success === true) return "success";
  if (item.success === false) return "failed";
  return "pending";
};

/**
 * NEW: freeWithSku linkage helper.
 *
 * A digital item and one or more physical items are "associated" when
 * they share the same `freeWithSku` value (this is how the backend ties
 * a free/bundled digital reward to the physical product that unlocks it).
 *
 * Given a digital item, this looks at every physical item sharing its
 * freeWithSku and reduces their vend results to a single verdict:
 *   - "failed"  → at least one associated physical item failed to vend
 *   - "success" → ALL associated physical items vended successfully
 *   - "pending" → still vending / not yet resolved
 *   - null      → this digital item has no freeWithSku linkage at all
 *
 * NOTE: if a digital item is linked to multiple physical units (e.g. you
 * bought 2 of the triggering SKU), we require all of them to succeed
 * before auto-claiming — change `.every` to `.some` below if the desired
 * behaviour is "any one success is enough".
 */
// Type-safe equality: treats 1234 and "1234" as the same value. This is a
// common real-world footgun — one side of the API sends freeWithSku as a
// number, the other as a string, and a strict `===` silently never matches.
const sameSku = (a, b) => {
  if (a === undefined || a === null || b === undefined || b === null) return false;
  return String(a) === String(b);
};

const getLinkedPhysicalStatus = (digitalItem, physicalItems) => {
  if (digitalItem.freeWithSku === undefined || digitalItem.freeWithSku === null) {
    return null;
  }

  // Primary match: physical item's own freeWithSku equals the digital
  // item's freeWithSku (both point at the same campaign/trigger value).
  // Fallback match: some backends only ever put freeWithSku on the DIGITAL
  // item, and its value is actually the triggering physical product's own
  // id/variantId/skuGroupId — so we also check those.
  const linked = physicalItems.filter(
    (p) =>
      sameSku(p.freeWithSku, digitalItem.freeWithSku) ||
      sameSku(p.id, digitalItem.freeWithSku) ||
      sameSku(p.variantId, digitalItem.freeWithSku) ||
      sameSku(p.skuGroupId, digitalItem.freeWithSku)
  );

  if (linked.length === 0) {
    // eslint-disable-next-line no-console
    logger.warn(
      `[VendingProgress] No physical item found linked to digital item "${digitalItem.productName}" (freeWithSku=${digitalItem.freeWithSku}). ` +
        `It will fall back to server claimStatus only and WON'T reflect physical vend failures. Check the field mapping.`
    );
    return null;
  }
  if (linked.some((p) => p.success === false)) return "failed";
  if (linked.every((p) => p.success === true)) return "success";
  return "pending";
};

/**
 * Determines the claim status of a DIGITAL item.
 * Driven by `digitalLineItemList[].claimStatus` from the API,
 * PLUS the freeWithSku linkage to physical items (auto-claim path),
 * PLUS any in-flight auto-claim API call we've kicked off ourselves.
 *
 * Priority order:
 *   1. Linked physical item(s) failed  → "failed" (never attempt claim)
 *   2. Linked physical item(s) succeeded:
 *        - already CLAIMED on server        → "success"
 *        - our own auto-claim call succeeded → "success"
 *        - our own auto-claim call failed    → "failed"
 *        - our own auto-claim call in flight  → "processing"
 *        - requires email (needs user input) → "ready" (popup will handle it)
 *        - otherwise (about to be auto-claimed) → "processing"
 *   3. Linked physical item(s) still pending → "pending"
 *   4. No linkage at all → fall back to original claimStatus-only logic
 *
 * Add more conditions here as needed.
 */
const getDigitalStatus = (item, physicalItems = [], autoClaimStatus = {}) => {
  if (!item.vendingComplete) return "pending";

  const claim = item.digitalLineItemList?.[0];
  const linked = getLinkedPhysicalStatus(item, physicalItems);

  if (linked === "failed") return "failed";

  if (linked === "success") {
    if (claim?.claimStatus === "CLAIMED") return "success";

    const auto = autoClaimStatus[item.uniqueKey];
    if (auto === "success") return "success";
    if (auto === "failed") return "failed";
    if (auto === "processing") return "processing";

    if (item.isEmailRequired) return "ready"; // waiting on the email popup flow
    return "processing"; // about to be auto-claimed
  }

  if (linked === "pending") return "pending";

  // No freeWithSku linkage. Still prefer our own in-flight/just-completed
  // claim signal over claim.claimStatus — autoClaimStatus updates the
  // instant a claim call resolves, while claim.claimStatus only updates
  // after the next fetchOrderDetails() (e.g. once the success dialog is
  // dismissed), so without this check an item sits on "ready"/"Tap to
  // claim" for a while right after it was actually claimed.
  const auto = autoClaimStatus[item.uniqueKey];
  if (auto === "success") return "success";
  if (auto === "failed") return "failed";
  if (auto === "processing") return "processing";

  if (!claim) return "pending";
  switch (claim.claimStatus) {
    case "CLAIMED":
      return "success";
    case "EXPIRED":
    case "FAILED":
      return "failed";
    case "PROCESSING":
      return "processing";
    case "UNCLAIMED":
    default:
      return "ready"; // ready to be claimed
  }
};

// ─────────────────────────────────────────────
// STATUS ICON — renders the right icon per status
// ─────────────────────────────────────────────
const StatusIcon = ({ status }) => {
  switch (status) {
    case "success":
      return <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 30 }} />;
    case "failed":
      return <CancelIcon sx={{ color: "#f44336", fontSize: 30 }} />;
    case "ready":
      return (
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 30 }} />
        </Box>
      );
    case "processing":
      return <CircularProgress size={28} thickness={4} sx={{ color: "#2196f3" }} />;
    case "pending":
    default:
      return <CircularProgress size={28} thickness={4} sx={{ color: "#fbc02d" }} />;
  }
};

// ─────────────────────────────────────────────
// SECTION HEADER — Physical / Digital label row
// ─────────────────────────────────────────────
const SectionHeader = ({ icon, label }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    sx={{ pt: 1.5, pb: 0.5 }}
  >
    <Box display="flex" alignItems="center" gap={0.75}>
      {icon}
      <Typography variant="body2" sx={{ fontWeight: 700, color: "#333", fontSize: 13 }}>
        {label}
      </Typography>
    </Box>
    <Typography variant="body2" sx={{ fontWeight: 700, color: "#333", fontSize: 13 }}>
      Status
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────
// ITEM ROW — single product in the list
// ─────────────────────────────────────────────
const ItemRow = ({ item, status, isDigital, onRetryClaim }) => {
  // Only digital items that haven't successfully claimed and aren't
  // mid-flight are worth letting the user tap to retry — retrying a
  // "success" or "processing" item makes no sense, and "pending" means
  // the physical vend itself hasn't resolved yet so there's nothing to
  // claim regardless.
  const isRetryable = isDigital && (status === "failed" || status === "ready") && !!onRetryClaim;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      onClick={isRetryable ? () => onRetryClaim(item) : undefined}
      sx={{
        py: 1.5,
        borderBottom: "1px solid #eee",
        cursor: isRetryable ? "pointer" : "default",
        "&:hover": isRetryable ? { backgroundColor: "#fafafa" } : undefined,
      }}
    >
    <Box display="flex" alignItems="center">
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: "10px",
          overflow: "hidden",
          border: "1px solid #eee",
          mr: 1.5,
          flexShrink: 0,
          background: "#fafafa",
        }}
      >
        <img
          src={
            item.imageUrl?.startsWith("http")
              ? item.imageUrl
              : `https://daalchini.s3.ap-south-1.amazonaws.com/dcprod/${item.imageUrl}`
          }
          alt={item.productName}
          width="52"
          height="52"
          style={{ objectFit: "cover" }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 13 }}>
          {item.productName}
        </Typography>

        <Typography variant="body2" sx={{ color: "#666", fontSize: 12 }}>
          {item.offerPrice === 0 ? "Free" : `₹${item.offerPrice.toFixed(2)}`}
        </Typography>

        {/* Extra info per status — add more copy here as needed */}
        {isDigital && status === "ready" && (
          <Typography sx={{ fontSize: 11, color: "#4caf50", fontWeight: 500, mt: 0.3 }}>
            Ready to claim
          </Typography>
        )}
        {isDigital && status === "processing" && (
          <Typography sx={{ fontSize: 11, color: "#2196f3", fontWeight: 500, mt: 0.3 }}>
            Processing…
          </Typography>
        )}
        {isRetryable && (
          <Typography sx={{ fontSize: 11, color: "#009688", fontWeight: 500, mt: 0.3 }}>
            Tap to {status === "failed" ? "retry" : "claim"}
          </Typography>
        )}
      </Box>
    </Box>

    <StatusIcon status={status} />
  </Box>
  );
};

// ─────────────────────────────────────────────
// ERROR BANNER — shown when any item fails
// ─────────────────────────────────────────────
const ErrorBanner = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1,
      mx: 2,
      my: 1.5,
      p: 1.5,
      background: "#fff8e1",
      borderRadius: "10px",
      border: "1px solid #ffe082",
    }}
  >
    <Typography sx={{ fontSize: 18 }}>⚠️</Typography>
    <Typography sx={{ fontSize: 12, color: "#555", lineHeight: 1.5 }}>
      Something didn't go as expected. Don't worry — just tap the{" "}
      <strong>Help</strong> button below and we'll sort it out.
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const VendingProgress = () => {
  const navigate = useNavigate();
  const ReduxWebSocketData = useSelector((state) => state.WebSocket?.WebSocketData);
  const { wsData, startWebSocket } = useWebSocket();
  const { orderId } = useParams();
  const { makeApiRequest } = useApiRequest();

  const token = localStorage.getItem("UserJWTAuth");
  const deviceId = localStorage.getItem("deviceId");

  const [physicalItems, setPhysicalItems] = useState([]);
  const [digitalItems, setDigitalItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Email popup queue state ────────────────
  const [emailQueue, setEmailQueue] = useState([]);
  const [currentEmailItem, setCurrentEmailItem] = useState(null);
  const [emailPopupOpen, setEmailPopupOpen] = useState(false);
  const [emailQueueBuilt, setEmailQueueBuilt] = useState(false); // guard: build queue only once
  const [claimSuccessOpen, setClaimSuccessOpen] = useState(false);

  // ── NEW: auto-claim tracking ───────────────
  // Keyed by digital item's uniqueKey → "processing" | "success" | "failed".
  // Prevents firing duplicate auto-claim calls for the same item and lets
  // getDigitalStatus reflect an in-flight/just-completed auto-claim
  // immediately, without waiting on a full order refetch.
  const [autoClaimStatus, setAutoClaimStatus] = useState({});

  // ── Derived flags ──────────────────────────
  const allPhysicalDone =
    physicalItems.length === 0 ||
    physicalItems.every((i) => i.success !== null);

  const anyFailed =
    physicalItems.some((i) => i.success === false) ||
    digitalItems.some((i) => {
      const s = getDigitalStatus(i, physicalItems, autoClaimStatus);
      return s === "failed";
    });

  const physicalProgress =
    physicalItems.length > 0
      ? physicalItems.reduce((sum, i) => sum + (i.progress || 0), 0) /
        physicalItems.length
      : 0;

  const totalProgress = allPhysicalDone ? 100 : physicalProgress;

  // ── Handlers ──────────────────────────────
  const handleClose = () => navigate("/");
  const handleHome = () => navigate("/");
  const handleSupport = () => navigate(`/ordersearch?id=${orderId}`);

  // ── Parse API response ────────────────────
  // IMPORTANT: this runs on every fetchOrderDetails() call, including the
  // refetch we trigger after a successful claim/auto-claim (to pick up the
  // fresh claimStatus). It must NOT blindly reset success/progress/
  // vendingComplete each time, or the whole list flashes back to "pending"
  // (loading spinners) right after a claim succeeds — it should only ever
  // reset those fields the FIRST time an item is seen.
  const parseOrderData = (data) => {
    const lineItems = data.lineItems || [];
    logger.log("[VendingProgress] parseOrderData: raw lineItems", lineItems);

    setPhysicalItems((prevPhysical) => {
      const prevByKey = new Map(prevPhysical.map((p) => [p.uniqueKey, p]));

      const result = lineItems
        .filter((item) => !item.isDigital)
        .flatMap((item) =>
          Array.from({ length: item.unitCount || 1 }, (_, i) => {
            const uniqueKey = `physical-${item.id}-${i + 1}`;
            const existing = prevByKey.get(uniqueKey);
            return {
              ...item,
              progress: existing ? existing.progress : 0,
              success: existing ? existing.success : null,
              uniqueKey,
            };
          })
        );
      logger.log("[VendingProgress] parseOrderData: parsed physicalItems", result);
      return result;
    });

    setDigitalItems((prevDigital) => {
      const prevByKey = new Map(prevDigital.map((d) => [d.uniqueKey, d]));

      const result = lineItems
        .filter((item) => item.isDigital)
        .flatMap((item) =>
          Array.from({ length: item.unitCount || 1 }, (_, i) => {
            const uniqueKey = `digital-${item.id}-${i + 1}`;
            const existing = prevByKey.get(uniqueKey);
            return {
              ...item,
              // Keep vendingComplete true once it's been set — only a
              // brand-new item (first load) starts out false.
              vendingComplete: existing ? existing.vendingComplete : false,
              uniqueKey,
            };
          })
        );
      logger.log(
        "[VendingProgress] parseOrderData: parsed digitalItems",
        result.map((d) => ({
          uniqueKey: d.uniqueKey,
          isEmailRequired: d.isEmailRequired,
          freeWithSku: d.freeWithSku,
          claimStatus: d.digitalLineItemList?.[0]?.claimStatus,
        }))
      );
      return result;
    });
  };

  // ── WebSocket update handler ──────────────
  const handleWebSocketData = (data) => {
    const lineItem = data?.lineItem || data;
    if (!lineItem?.id) return;

    setPhysicalItems((prev) => {
      const updated = [...prev];
      const index = updated.findIndex(
        (i) => i.id === lineItem.id && i.success === null
      );
      if (index !== -1) {
        updated[index] = {
          ...updated[index],
          progress: 100,
          success: lineItem.success,
        };
      }
      return updated;
    });
  };

  // ── Fetch order ───────────────────────────
  const fetchOrderDetails = () => {
    if (!token || !orderId) return;
    makeApiRequest({
      //url: `http://localhost:5050/order-status/${orderId}`,
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/order/${orderId}`,

      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-device-id": deviceId || "",
      },
      handleSuccess: (res) => {
        const data = res?.data?.data || res?.data || {};
        parseOrderData(data);
        setLoading(false);

        const reduxOrderId = ReduxWebSocketData?.orderId || ReduxWebSocketData?.lineItem?.orderId;
        const isReduxDataForThisOrder =
          ReduxWebSocketData &&
          Object.keys(ReduxWebSocketData).length > 0 &&
          String(reduxOrderId) === String(orderId);

        if (isReduxDataForThisOrder) {
          handleWebSocketData(ReduxWebSocketData);
        } else {
          //const wsUrl = `ws://localhost:5050/consumerbe/status/payment?order_id=${orderId}`;
          const wsUrl=`${process.env.REACT_APP_WEB_SOCKET_BASE_URL}/consumerbe/status/payment?order_id=${orderId}`;
          startWebSocket(wsUrl, handleWebSocketData);
        }
      },
      handleError: (err) => {
        logger.error("Fetch Order Error:", err);
        setLoading(false);
      },
    });
  };

  // ── NEW: fire the auto-claim call for a single digital item ──
  // Hits the real claim endpoint:
  //   POST https://api-prod.daalchini.co.in/consumerbe/api/v1/digital/claim
  //   body: { digitalLineItemId, emailId }
  //
  // NOTE: the endpoint requires an emailId even for items that don't
  // strictly need one for our UI flow (isEmailRequired: false). We fall
  // back to the logged-in user's stored email if present, otherwise an
  // empty string — confirm with backend whether that's acceptable, or
  // whether the user's account email should be fetched from a profile
  // API instead before auto-claiming.
  const autoClaimDigitalItem = (item) => {
    const claim = item.digitalLineItemList?.[0];
    const digitalLineItemId = claim?.digitalLineItemId ?? claim?.digitalLineItemClaimId;
    if (!digitalLineItemId) return;

    setAutoClaimStatus((prev) => ({ ...prev, [item.uniqueKey]: "processing" }));

    const userEmail = localStorage.getItem("UserEmail") || "";

    makeApiRequest({
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v1/digital/claim`,
      //url: `http://localhost:5050/consumerbe/api/v1/digital/claim`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "content-type": "application/json",
        "x-device-id": deviceId || "",
      },
      data: {
        digitalLineItemId,
        emailId: userEmail,
      },
      handleSuccess: () => {
        setAutoClaimStatus((prev) => ({ ...prev, [item.uniqueKey]: "success" }));
        // Pull the fresh claimStatus ("CLAIMED") down from the server too.
        fetchOrderDetails();
      },
      handleError: (err) => {
        logger.error("Auto-claim Error:", err);
        setAutoClaimStatus((prev) => ({ ...prev, [item.uniqueKey]: "failed" }));
      },
    });
  };

  // ── Once all physical done → unlock digital + build email queue ─
  useEffect(() => {
    if (allPhysicalDone && digitalItems.length > 0) {
      setDigitalItems((prev) =>
        prev.map((item) => ({ ...item, vendingComplete: true }))
      );

      if (!emailQueueBuilt) {
        // Queue every still-unclaimed digital item once its linked
        // physical item (if any) has resolved — the drain-queue effect
        // below decides popup vs. direct auto-claim based on
        // isEmailRequired. Skip items tied to a physical item that
        // failed to vend — nothing to claim there.
        const needsQueueing = digitalItems.filter((item) => {
          const claim = item.digitalLineItemList?.[0];
          if (claim?.claimStatus !== "UNCLAIMED") {
            logger.log(`[VendingProgress] queue-build: skip ${item.uniqueKey} — claimStatus is ${claim?.claimStatus}, not UNCLAIMED`);
            return false;
          }

          const linked = getLinkedPhysicalStatus(item, physicalItems);
          if (linked === "failed") {
            logger.log(`[VendingProgress] queue-build: skip ${item.uniqueKey} — linked physical failed`);
            return false;
          }
          logger.log(`[VendingProgress] queue-build: QUEUEING ${item.uniqueKey} (isEmailRequired=${item.isEmailRequired})`);
          return true;
        });

        logger.log("[VendingProgress] queue-build: final emailQueue", needsQueueing.map((i) => i.uniqueKey));

        if (needsQueueing.length > 0) {
          setEmailQueue(needsQueueing);
        }
        setEmailQueueBuilt(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPhysicalDone, digitalItems.length]);

  // NOTE: auto-claiming for isEmailRequired: false items now happens
  // inside the drain-queue effect below (it's the single place that
  // decides popup vs. direct auto-claim), so there's no separate
  // standalone auto-claim effect anymore — that would risk firing the
  // claim twice for the same item.

  // ── Drain the email queue one item at a time ─
  useEffect(() => {
    logger.log("emailQueue", emailQueue, "emailQueue emailPopupOpen", emailPopupOpen);
    if (emailQueue.length === 0 || emailPopupOpen) return;

    const next = emailQueue[0];

    if (next.isEmailRequired === true) {
      setCurrentEmailItem(next);
      setEmailPopupOpen(true);
      // Dequeuing happens in handleEmailPopupClose once the popup
      // actually closes — not here.
    } else {
      // No popup needed. Auto-claim directly, but guard against firing
      // twice for the same item, and dequeue it immediately — otherwise
      // emailQueue[0] stays this same item forever and the queue can
      // never advance to whatever's behind it.
      const current = autoClaimStatus[next.uniqueKey];
      if (current !== "processing" && current !== "success") {
        autoClaimDigitalItem(next);
      }
      setEmailQueue((prev) => prev.slice(1));
    }
  }, [emailQueue, emailPopupOpen]);

  const handleEmailPopupClose = () => {
    setEmailPopupOpen(false);
    // Only dequeue if the item we're closing is actually the head of the
    // queue — a manually-retried item (see handleManualClaim below) may
    // not be, and we shouldn't pop an unrelated item off the queue.
    setEmailQueue((prev) =>
      prev.length > 0 && prev[0].uniqueKey === currentEmailItem?.uniqueKey
        ? prev.slice(1)
        : prev
    );
    setCurrentEmailItem(null);
    fetchOrderDetails();
  };

  // ── Claim after email/OTP verify ──────────
  // Same endpoint as autoClaimDigitalItem, plus the verified emailId. Kept
  // separate since this path additionally shows the success dialog before
  // the popup/queue actually closes (see closeOnVerify={false} below).
  const claimDigital = (lineItemId, email) => {
    setAutoClaimStatus((prev) => ({ ...prev, [currentEmailItem?.uniqueKey]: "processing" }));

    makeApiRequest({
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v1/digital/claim`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "content-type": "application/json",
        "x-device-id": deviceId || "",
      },
      data: {
        digitalLineItemId: lineItemId,
        emailId: email,
      },
      handleSuccess: () => {
        setAutoClaimStatus((prev) => ({ ...prev, [currentEmailItem?.uniqueKey]: "success" }));
        setClaimSuccessOpen(true);
      },
      handleError: (err) => {
        setAutoClaimStatus((prev) => ({ ...prev, [currentEmailItem?.uniqueKey]: "failed" }));
        const msg =
          err?.response?.data?.status?.message || "Claim failed. Please try again.";
        toast.error(msg);
      },
    });
  };

  const handleClaimSuccessClose = () => {
    setClaimSuccessOpen(false);
    handleEmailPopupClose();
  };

  // ── User-initiated retry ──────────────────
  // Lets someone tap a digital item that's stuck "failed" or "ready" (e.g.
  // they dismissed the popup without finishing, or a claim attempt failed)
  // and try again — same isEmailRequired branching as the automatic flow.
  const handleManualClaim = (item) => {
    const claim = item.digitalLineItemList?.[0];
    if (claim?.claimStatus === "CLAIMED") return; // nothing to do

    if (item.isEmailRequired) {
      if (emailPopupOpen) return; // another popup is already showing
      setCurrentEmailItem(item);
      setEmailPopupOpen(true);
    } else {
      // Clear any previous failed status so the UI shows "processing"
      // again while this retry is in flight.
      setAutoClaimStatus((prev) => ({ ...prev, [item.uniqueKey]: undefined }));
      autoClaimDigitalItem(item);
    }
  };

  // ── Watch Redux WebSocket ─────────────────
  useEffect(() => {
    if (!ReduxWebSocketData || Object.keys(ReduxWebSocketData).length === 0) return;
    const reduxOrderId = ReduxWebSocketData?.orderId || ReduxWebSocketData?.lineItem?.orderId;
    if (String(reduxOrderId) === String(orderId)) {
      handleWebSocketData(ReduxWebSocketData);
    }
  }, [ReduxWebSocketData]);

  useEffect(() => {
    logger.log("[VendingProgress] orderId changed → resetting per-order state", { orderId });
    // Reset every piece of per-order state before fetching. Without this,
    // navigating from one order to another (same component instance, only
    // the orderId route param changes) can leave a stale currentEmailItem/
    // emailQueue from the PREVIOUS order sitting around, popping the email
    // drawer open even though the new order's items are all
    // isEmailRequired: false.
    setLoading(true);
    setPhysicalItems([]);
    setDigitalItems([]);
    setEmailQueue([]);
    setCurrentEmailItem(null);
    setEmailPopupOpen(false);
    setEmailQueueBuilt(false);
    setAutoClaimStatus({});

    fetchOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // ── Loading screen ────────────────────────
  if (loading) {
    return (
      <Box height="100vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  logger.log("[VendingProgress] RENDER: currentEmailItem =", currentEmailItem ? {
    uniqueKey: currentEmailItem.uniqueKey,
    productName: currentEmailItem.productName,
    isEmailRequired: currentEmailItem.isEmailRequired,
  } : null, "emailPopupOpen =", emailPopupOpen);

  // ── Render ────────────────────────────────
  return (
    <>
      {currentEmailItem && (
        <>
          <SuccessClaimedDialog open={claimSuccessOpen} onClose={handleClaimSuccessClose} />
          <EmailVerifySheet
            open={emailPopupOpen}
            onClose={handleEmailPopupClose}
            title={currentEmailItem.productName}
            subtitle={currentEmailItem.fulfilmentType}
            imageUrl={
              currentEmailItem.imageUrl?.startsWith("http")
                ? currentEmailItem.imageUrl
                : `${process.env.REACT_APP_IMAGE_BASE_URL}/${currentEmailItem.imageUrl}`
            }
            onVerified={(email) =>
              claimDigital(
                currentEmailItem.digitalLineItemList?.[0]?.digitalLineItemClaimId,
                email
              )
            }
            closeOnVerify={false}
          />
        </>
      )}

      {/* Top Bar */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={2}
        sx={{
          background: "#fae14b",
          position: "fixed",
          width: "100%",
          zIndex: 999,
          top: 0,
        }}
      >
        <Box display="flex" alignItems="center">
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 500, ml: 1 }}>
            Vending Progress
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          paddingTop: "66px",
        }}
      >
        {/* Progress Bar */}
        <LinearProgress
          variant="determinate"
          value={totalProgress}
          sx={{
            height: 6,
            backgroundColor: "#f0f0f0",
            "& .MuiLinearProgress-bar": { backgroundColor: "#FCD947" },
          }}
        />

        {/* Order Info */}
        <Box sx={{ px: 2, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="body2" sx={{ color: "#555" }}>
              Order ID:{" "}
              <Typography component="span" sx={{ color: "#1a237e", fontWeight: 600 }}>
                {orderId}
              </Typography>
            </Typography>
            <Typography variant="body2" sx={{ color: "#555", mt: 0.5 }}>
              Number Of Products:{" "}
              <Typography component="span" sx={{ color: "#1a237e", fontWeight: 600 }}>
                {(physicalItems.length + digitalItems.length) || 0}
              </Typography>
            </Typography>
          </Box>

          {/* Circular Progress */}
          <Box position="relative" display="inline-flex">
            <CircularProgress
              variant="determinate"
              value={100}
              size={55}
              thickness={4}
              sx={{ color: "#e0e0e0", position: "absolute", left: 0 }}
            />
            <CircularProgress
              variant="determinate"
              value={totalProgress}
              size={55}
              thickness={4}
              sx={{ color: "#338c0aff" }}
            />
            <Box
              top={0} left={0} bottom={0} right={0}
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#000" }}>
                {Math.round(totalProgress)}%
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Product Lists */}
        <Box flexGrow={1} overflow="auto" sx={{ px: 2, py: 1 }}>

          {/* ── Physical Items ── */}
          {physicalItems.length > 0 && (
            <>
              <SectionHeader label="Physical Product" />
              <Divider />
              {physicalItems.map((item) => (
                <ItemRow
                  key={item.uniqueKey}
                  item={item}
                  status={getPhysicalStatus(item)}
                  isDigital={false}
                />
              ))}
            </>
          )}

          {/* ── Digital Items ── */}
          {digitalItems.length > 0 && (
            <>
              <SectionHeader label="Digital Product" />
              <Divider />
              {digitalItems.map((item) => (
                <ItemRow
                  key={item.uniqueKey}
                  item={item}
                  status={getDigitalStatus(item, physicalItems, autoClaimStatus)}
                  isDigital={true}
                  onRetryClaim={handleManualClaim}
                />
              ))}
            </>
          )}
        </Box>

        {/* Error Banner */}
        {anyFailed && <ErrorBanner />}

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-evenly",
            borderTop: "1px solid #ddd",
            py: 2,
          }}
        >
          <Button
            onClick={handleHome}
            variant="outlined"
            startIcon={<HomeIcon />}
            sx={{
              color: "#009688",
              borderColor: "#009688",
              textTransform: "none",
              fontWeight: 600,
              width: "40%",
            }}
          >
            Home
          </Button>
          <Button
            onClick={handleSupport}
            variant="contained"
            startIcon={<HeadsetMicIcon />}
            sx={{
              backgroundColor: "#e0f2f1",
              color: "#009688",
              textTransform: "none",
              fontWeight: 600,
              width: "40%",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#d0ece9" },
            }}
          >
            Help
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default VendingProgress;